import { useEffect, useState } from 'react';
import { generatePath, useNavigate, useSearchParams } from 'react-router';
import type { CardMenuItem } from '@tempmee/core';
import { AdditionalDetails, LargeShiftDetails } from '@tempmee/react-blocks';
import { OutlinePencilIcon, OutlineTrashIcon } from '@tempmee/react-icons';
import history from '~/browserHistory';
import OpenJobScreenSwitch from '~/components/counterJobs/OpenJobScreenSwitch';
import AnimatedCloseCircle from '~/components/shared/AnimatedCloseCircle';
import ConfirmPopup from '~/components/shared/ConfirmPopup';
import {
  confirmSourceFromEmailNotification,
  setConfirmCandidateSource,
} from '~/domain/ConfirmCandidateSource';
import { useCancelJobMutation } from '~/domain/Jobs';
import { buildEngagementContext, parseNotificationParams } from '~/domain/OfficeNotification';
import { useOfficeNotificationLanding } from '~/hooks/useOfficeNotificationLanding';
import {
  fireOfficeNotificationCandidateApplied,
  fireOfficeNotificationCandidateAppliedCounter,
} from '~/posthogs/events';
import { routes } from '~/routes';
import type { IShiftDetailsPageProps } from '../shiftDetailsPages';
import { toAdditionalDetailsRows } from '../shared/utils/shiftPanelsProps';
import { ShiftDetailsPageShell } from '../ShiftDetailsPageShell';
import { InvitePanel } from './components/InvitePanel';
import {
  toShiftSummary,
  type TOpenShiftJob,
} from './utils/openShiftDetailsMappers';

const CANCEL_DESCRIPTION =
  'Cancelling an open shift means that it will no longer be available to professionals around ' +
  'your office to accept and potentially work.';

/**
 * The redesigned OPEN Shift Details page body.
 *
 * Composed entirely from design-system blocks — Invite Professionals on the
 * left, the shift summary and Additional Information on the right — with this
 * component owning only the data mapping, the header actions and the flows they
 * open. Reached through `ShiftDetailsRedesignGate`, which has already loaded the
 * job and matched its status, so there is no loading or not-found state here.
 */
function OpenShiftDetailsRedesign({ job }: IShiftDetailsPageProps) {
  const openShift = job as TOpenShiftJob;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { mutate: cancelJob } = useCancelJobMutation();
  const [isCancelOpen, setIsCancelOpen] = useState(false);

  /*
   * Email-landing attribution, duplicated from `OpenJobScreenSwitch` (GOTU-4906)
   * rather than extracted: with the flag on this page replaces that switch, and
   * the switch has to keep a zero diff so the flag-off path — including the
   * GOTU-5384 test/control split — stays exactly as it is today.
   *
   * Read during render, before any effect below can strip the params from the
   * URL, and gated on `origin` so only a real email landing (`utm_source=email`)
   * is attributed to email.
   */
  const { origin, notification } = parseNotificationParams(searchParams);
  const emailSource =
    origin === 'email' ? confirmSourceFromEmailNotification(notification) : undefined;

  useEffect(() => {
    if (!emailSource) {
      return;
    }

    setConfirmCandidateSource({ source: emailSource, shiftId: job.id });
    // Fires once per landing: a later re-render, after the params are stripped,
    // must not clear what this recorded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
   * The `office_notification` landing events the legacy control arm
   * (`OpenJobScreen`) fires. The GOTU-5384 test arm never picked them up; this
   * page replaces both arms, so it keeps the one that reports.
   */
  useOfficeNotificationLanding({
    fallbackKey: 'candidate_applied',
    handlers: {
      candidate_applied: ({ origin: linkOrigin, clickedAt, shiftIds, professionalId }) =>
        fireOfficeNotificationCandidateApplied({
          origin: linkOrigin,
          shiftId: shiftIds[0] ?? job.id,
          professionalId,
          engagement: buildEngagementContext({ clickedAt, shiftStartAt: job.job_datetime }),
        }),
      candidate_applied_counter: ({ origin: linkOrigin, clickedAt, shiftIds, professionalId }) =>
        fireOfficeNotificationCandidateAppliedCounter({
          origin: linkOrigin,
          shiftId: shiftIds[0] ?? job.id,
          professionalId,
          engagement: buildEngagementContext({ clickedAt, shiftStartAt: job.job_datetime }),
        }),
    },
  });

  const actions: CardMenuItem[] = [
    {
      label: 'Edit',
      icon: <OutlinePencilIcon />,
      testID: 'shift-details-edit-action',
      onSelect: () => history.push(generatePath(routes.editOpenJob, { jobId: job.id })),
    },
    {
      label: 'Cancel',
      icon: <OutlineTrashIcon />,
      testID: 'shift-details-cancel-action',
      onSelect: () => setIsCancelOpen(true),
    },
  ];

  return (
    <>
      <ShiftDetailsPageShell
        title="Open Shift Details"
        actions={actions}
        onBack={() => navigate(-1)}
        summary={<LargeShiftDetails {...toShiftSummary(openShift)} />}
        primary={<InvitePanel job={job} />}
        sidebar={
          <AdditionalDetails
            variant="open-confirm"
            {...toAdditionalDetailsRows(openShift)}
            defaultOpen={false}
          />
        }
      />

      {isCancelOpen && (
        <ConfirmPopup
          Icon={() => <AnimatedCloseCircle />}
          title="Are you sure you want to cancel?"
          description={CANCEL_DESCRIPTION}
          leftButtonText="Go Back"
          leftButtonAction={() => setIsCancelOpen(false)}
          leftButtonDataCy="cancel-popup-go-back"
          rightButtonText="Cancel Shift"
          rightButtonDataCy="confirm-cancel-shift"
          rightButtonAction={() => {
            cancelJob({ jobId: job.id, redirect: true });
            setIsCancelOpen(false);
          }}
        />
      )}
    </>
  );
}

/**
 * OPEN Shift Details, as registered in `SHIFT_DETAILS_PAGES`.
 *
 * An onsite interview is a different page: its candidates come from
 * `/eligible-candidates`, which does not serve the ratings, skills or
 * availability the invite block renders (GOTU-5501, blocked on the backend).
 * Until it does, perm interviews stay on the screen they have today — which also
 * keeps the GOTU-5384 experiment resolving for them exactly as it does now.
 *
 * The split is a component boundary rather than an early return so the redesign's
 * landing-attribution effects never run on the delegated path, where the legacy
 * screen fires them itself.
 */
export function OpenShiftDetailsPage({ job }: IShiftDetailsPageProps) {
  if (job.isOnsiteInterview) {
    return <OpenJobScreenSwitch />;
  }

  return <OpenShiftDetailsRedesign job={job} />;
}
